import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  DialogContainer,
  Divider,
  LinkText as LinkTextBase,
  List,
  ListItem,
  Typography,
} from '@frontierag/frontier-ui-library';
import type { ILinkText } from '@frontierag/frontier-ui-library';
import { Warning as WarningIcon } from '@frontierag/frontier-ui-library/icons';
import { useAppSettings } from 'contexts/App/useAppSettings';
import { v4 as uuidv4 } from 'uuid';

import { styles } from './styles';
import { type IActionRequiredModalProps, ProductContext } from './types';
import usePhotoIdEmailStatus, { type PhotoIdEmailStatus } from './usePhotoIdEmailStatus';

const LinkText = LinkTextBase as unknown as FC<
  ILinkText & { component?: 'button'; type?: 'button'; onClick?: () => void }
>;

const EMAIL_STATUS_ALERT_SEVERITY = {
  sent: 'success',
  noMyFarm: 'warning',
  alreadySent: 'warning',
  error: 'error',
} as const;

const ActionRequiredModal: React.FC<IActionRequiredModalProps> = ({
  open,
  onClose,
  products = [],
  limusPerformProducts = [],
  customerReference,
  representativeReference,
}) => {
  const { t } = useTranslation('ActionRequired', { keyPrefix: 'dialog' });
  const { FERTILISER: { liquidNotificationDateRange } = {} } = useAppSettings();

  const allProducts = [
    { context: ProductContext.photoID, products },
    { context: ProductContext.limusPerform, products: limusPerformProducts },
  ].filter((p) => !!p.products.length);

  const hasPhotoIdContext = allProducts.some(({ context }) => context === ProductContext.photoID);

  const { emailStatus, sendEmail } = usePhotoIdEmailStatus({
    enabled: open && hasPhotoIdContext,
    customerReference,
    representativeReference,
  });

  // Tracks which resolved status the user has already dismissed the banner for, so a new send shows a fresh banner.
  const [dismissedStatus, setDismissedStatus] = useState<PhotoIdEmailStatus | null>(null);

  // 'alreadySent' must stay clickable (DOT-4150 AC5) so the user can resend for a new order
  const isLinkActive =
    emailStatus === 'idle' || emailStatus === 'error' || emailStatus === 'alreadySent';
  const alertSeverity =
    EMAIL_STATUS_ALERT_SEVERITY[emailStatus as keyof typeof EMAIL_STATUS_ALERT_SEVERITY];
  const showAlert = !!alertSeverity && dismissedStatus !== emailStatus;

  return (
    <DialogContainer
      open={open}
      onClose={onClose}
      customHeader={<Header allProducts={allProducts} />}
      closeLabel={t('confirmBtn')}
      closeButtonProps={{
        variant: 'contained',
      }}
      disableBackDropClose={false}
    >
      {allProducts.map(({ context, products }, index) => {
        const isLast = index === allProducts.length - 1;

        return (
          <Box key={context}>
            {allProducts.length > 1 && (
              <Typography variant="subtitle3" data-testid={`${context}-subtitle`}>
                {t('subtitle', { context, index: index + 1 })}
              </Typography>
            )}
            <Typography variant="body2" color="grey.L10" data-testid={`${context}-description`}>
              {t('description', {
                context,
                startDate: liquidNotificationDateRange?.startDayMonth,
                endDate: liquidNotificationDateRange?.endDayMonth,
              })}
            </Typography>
            <Box sx={styles.box}>
              <List sx={styles.ul}>
                {products.map((product) => (
                  <ListItem key={uuidv4()} sx={styles.li}>
                    <Typography variant="body2" fontWeight="700">
                      {product}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
            {context === ProductContext.photoID && (
              <Box sx={styles.emailSection}>
                <Typography variant="body2" color="grey.L10">
                  {t('sendEmailLinkPrompt')}
                </Typography>
                {showAlert && (
                  <Alert
                    severity={alertSeverity}
                    sx={styles.emailAlert}
                    data-testid="email-status-alert"
                    onClose={() => setDismissedStatus(emailStatus)}
                  >
                    {t(`emailStatus_${emailStatus}`)}
                  </Alert>
                )}
                {isLinkActive ? (
                  <Box sx={styles.emailLinkWrapper}>
                    <LinkText
                      preset="primary"
                      component="button"
                      type="button"
                      onClick={sendEmail}
                      data-testid="send-email-link"
                    >
                      {t('sendEmailLink')}
                    </LinkText>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="grey.L60"
                    data-testid="send-email-link"
                    sx={styles.emailLinkDisabled}
                  >
                    {t('sendEmailLink')}
                  </Typography>
                )}
              </Box>
            )}
            {!isLast && <Divider sx={{ my: 1.5 }} />}
          </Box>
        );
      })}
    </DialogContainer>
  );
};

const Header = ({ allProducts }) => {
  const { t } = useTranslation('ActionRequired', { keyPrefix: 'dialog' });

  let context = ProductContext.multi;
  if (allProducts.length === 1) context = allProducts[0].context;

  return (
    <Box sx={styles.header}>
      <WarningIcon width={24} sx={styles.warningIcon} />
      <Typography variant="h3">{t('title', { context })}</Typography>
    </Box>
  );
};

export default ActionRequiredModal;
